/**
 * Image Carousel with Text - Custom Element
 * A carousel component for displaying rotating images alongside static text content
 */

if (!customElements.get('image-carousel')) {
  class ImageCarousel extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isTransitioning = false;
      this.autoplayTimer = null;
      this.autoplayPaused = false;
      this.touchStartX = 0;
      this.swipeThreshold = 50;
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    init() {
      this.slides = this.querySelectorAll('.image-carousel__slide');
      this.dots = this.querySelectorAll('.image-carousel__dot');
      this.prevBtn = this.querySelector('.image-carousel__arrow--prev');
      this.nextBtn = this.querySelector('.image-carousel__arrow--next');

      if (this.slides.length <= 1) return;

      // Get settings from data attributes
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.transitionEffect = this.dataset.transition || 'fade';

      // Bind event handlers
      this.bindEvents();

      // Start autoplay if enabled
      if (this.autoplayEnabled) {
        this.startAutoplay();
        this.addVisibilityObserver();
      }
    }

    bindEvents() {
      // Navigation arrows
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.showPrevSlide());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.showNextSlide());
      }

      // Pagination dots
      this.dots.forEach((dot) => {
        dot.addEventListener('click', (e) => {
          const index = parseInt(e.currentTarget.dataset.index, 10);
          this.goToSlide(index);
        });
      });

      // Touch events for swipe support
      this.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      this.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });

      // Pause autoplay on hover/focus
      if (this.autoplayEnabled) {
        this.addEventListener('mouseenter', () => this.pauseAutoplay());
        this.addEventListener('mouseleave', () => this.resumeAutoplay());
        this.addEventListener('focusin', () => this.pauseAutoplay());
        this.addEventListener('focusout', () => this.resumeAutoplay());
      }

      // Shopify editor events
      document.addEventListener('shopify:section:load', this.handleSectionLoad.bind(this));
      document.addEventListener('shopify:block:select', this.handleBlockSelect.bind(this));
    }

    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
    }

    handleTouchEnd(e) {
      const touchEndX = e.changedTouches[0].screenX;
      const diff = this.touchStartX - touchEndX;

      if (Math.abs(diff) > this.swipeThreshold) {
        if (diff > 0) {
          this.showNextSlide();
        } else {
          this.showPrevSlide();
        }
      }
    }

    showNextSlide() {
      let nextIndex = this.currentIndex + 1;
      if (nextIndex >= this.slides.length) {
        nextIndex = 0;
      }
      this.goToSlide(nextIndex, 'next');
    }

    showPrevSlide() {
      let prevIndex = this.currentIndex - 1;
      if (prevIndex < 0) {
        prevIndex = this.slides.length - 1;
      }
      this.goToSlide(prevIndex, 'prev');
    }

    goToSlide(index, direction = null) {
      if (index === this.currentIndex || this.isTransitioning) return;

      this.isTransitioning = true;

      // Determine direction if not provided
      if (!direction) {
        direction = index > this.currentIndex ? 'next' : 'prev';
      }

      const currentSlide = this.slides[this.currentIndex];
      const nextSlide = this.slides[index];

      // Handle slide transition effect
      if (this.transitionEffect === 'slide') {
        currentSlide.classList.add(direction === 'next' ? 'slide-out-left' : 'slide-out-right');
      }

      // Update active states
      currentSlide.classList.remove('is-active');
      nextSlide.classList.add('is-active');

      // Update dots
      this.updateDots(index);

      // Clean up after transition
      setTimeout(() => {
        currentSlide.classList.remove('slide-out-left', 'slide-out-right');
        this.currentIndex = index;
        this.isTransitioning = false;
      }, 600);

      // Reset autoplay timer
      if (this.autoplayEnabled && !this.autoplayPaused) {
        this.resetAutoplay();
      }
    }

    updateDots(activeIndex) {
      this.dots.forEach((dot, index) => {
        if (index === activeIndex) {
          dot.classList.add('is-active');
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.classList.remove('is-active');
          dot.removeAttribute('aria-current');
        }
      });
    }

    startAutoplay() {
      this.autoplayPaused = false;
      this.autoplayTimer = setInterval(() => {
        this.showNextSlide();
      }, this.autoplaySpeed);
    }

    pauseAutoplay() {
      this.autoplayPaused = true;
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    resumeAutoplay() {
      if (this.autoplayEnabled && this.autoplayPaused) {
        this.startAutoplay();
      }
    }

    resetAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
      }
      this.autoplayTimer = setInterval(() => {
        this.showNextSlide();
      }, this.autoplaySpeed);
    }

    addVisibilityObserver() {
      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.resumeAutoplay();
          } else {
            this.pauseAutoplay();
          }
        });
      }, { threshold: 0.3 });

      observer.observe(this);
    }

    handleSectionLoad(e) {
      if (e.detail.sectionId !== this.dataset.sectionId) return;
      this.cleanup();
      this.init();
    }

    handleBlockSelect(e) {
      if (!this.contains(e.target)) return;

      const blockElement = e.target.closest('.image-carousel__slide');
      if (blockElement) {
        const index = parseInt(blockElement.dataset.index, 10);
        if (!isNaN(index)) {
          this.pauseAutoplay();
          this.goToSlide(index);
        }
      }
    }

    cleanup() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  }

  customElements.define('image-carousel', ImageCarousel);
}
